import { AlertController, LoadingController, ModalController } from '@ionic/angular';

import { Component, OnInit } from '@angular/core';

import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { AnalyticsServicesNameEnum } from 'src/app/core/services/analytics/enums/analytics-services-name.enum';
import { BasePage } from 'src/app/pages/base.page';
import { ConsumerAccountService } from 'src/app/pages/consumer-account/services/consumer-account.service';
import { PrefillLmjInfoFromSessionBannerStatus } from 'src/app/pages/flex/enums/prefill-lmj-info-from-session-banner-status.enum';
import { FlexJobsService } from 'src/app/pages/flex/services/flex-jobs.service';
import {
  POSTJOB_ORIGIN_QUERY_PARAM,
  POSTJOB_SOURCE_QUERY_PARAM,
  SCHEDULED_EVENT_DERIVED_UNIQUE_ID_PARAM,
  SCHEDULED_EVENT_OCCURRENCE_ID_PARAM,
} from 'src/app/pages/post-jobs/constants/post-job.constants';
import { PostJobQueryParams } from 'src/app/pages/post-jobs/models/post-job-query-params.model';
import { settings } from 'src/app/settings';
import { AutoCompleteDataSourceEnum } from 'src/app/shared/components/modals/auto-complete-modal/enums/auto-complete-data-source.enum';
import { AutoCompleteService } from 'src/app/shared/components/modals/auto-complete-modal/services/auto-complete.service';
import { flexServices } from 'src/app/shared/components/worker-service/worker-service-data';
import { EventTrackingEnum } from 'src/app/shared/enums/event-tracking.enum';
import { GQLErrorCodeEnum } from 'src/app/shared/enums/graphql-error-code.enum';
import { ConsumerAccount } from 'src/app/shared/models/gql/consumer-account.model';
import { JobCreateUpdateRequest } from 'src/app/shared/models/gql/job-create-update-request.model';
import { JobsService } from 'src/app/shared/services/jobs.service';
import { SoftBanService } from 'src/app/shared/services/soft-ban.service';
import { isGraphQLErrorCode } from 'src/app/shared/utilities/graphql-error-handling';

@Component({
  selector: 'app-flex-job-form',
  templateUrl: './form.page.html',
  styleUrls: ['./form.page.scss'],
})
export class FlexJobFormPage extends BasePage implements OnInit {
  // Class members
  public job;

  public submitAttempt = false;

  public form: UntypedFormGroup;

  public minutes: Array<number> = [0, 15, 30, 45];

  public dateFormat = 'DD/MM/YYYY'; // Used for start_date and end_date

  public timeFormat = 'DD-MM-YYYY HH:mm'; // Used for start_time and end_time

  public dayOptions = [
    {
      id: moment().format(this.dateFormat),
      name: 'Today (4 or more hours from now)',
    },
    {
      id: moment().add(1, 'days').format(this.dateFormat),
      name: 'Tomorrow',
    },
    {
      id: moment().add(2, 'days').format(this.dateFormat),
      name: moment().add(2, 'days').format('dddd'), // e.g. Wednesday
    },
  ];

  // error message key(backend error field) and value(to show in the error message) pair
  errorMessageKeyMapping = {
    title: 'title',
    job_description: 'job_description',
    consumer_description: 'consumer_description',
    'Start time': 'start_time',
  };

  // Object context to prefill last minute jobs with scheduled event details
  prefillLmjInfoFromSessionContext: {
    hasBeenLoadedAtLeastOnce: boolean;
    bannerStatus: PrefillLmjInfoFromSessionBannerStatus;
  } = {
    hasBeenLoadedAtLeastOnce: false,
    bannerStatus: PrefillLmjInfoFromSessionBannerStatus.PREFILL,
  };

  // Derived unique id of the cancelled scheduled event
  scheduledEventDerivedUniqueId: string;

  // Occurrence id of the cancelled scheduled event
  scheduledEventOccurrenceId: string;

  constructor(
    private _FB: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private analyticsService: AnalyticsService,
    private router: Router,
    private softBanService: SoftBanService,
    protected loadingCtrl: LoadingController,
    protected modalCtrl: ModalController,
    public alertCtrl: AlertController,
    public flexJobsService: FlexJobsService,
    public jobsService: JobsService,
    private consumerAccountService: ConsumerAccountService,
    private autoCompleteService: AutoCompleteService,
  ) {
    super('JobsFormPage');
  }

  async ngOnInit(): Promise<void> {
    this.job = {};
    this.form = this._FB.group({
      title: ['', Validators.compose([Validators.required, Validators.minLength(6)])],
      job_description: ['', Validators.compose([Validators.required, Validators.minLength(10)])],
      consumer_description: [''],
      job_location: ['', Validators.required],
      gender: [''],
      post_code_geo_id: [''],
      search_filter: this._FB.group({
        basic_search: [[]],
        advanced: this._FB.group({
          'users.gender': [],
        }),
        suburb: this._FB.group({
          idx: ['', Validators.required],
          txt: ['', Validators.required],
        }),
      }),
      start_date: ['', Validators.required],
      start_time: ['', Validators.required],
      end_date: [''],
      end_time: [''],
      duration: [4, [Validators.required, Validators.min(1), Validators.max(24)]],
    });
    this.getLocationDetails();
    this.prefillLmjInfoFromSession();

    // Log segment event
    this.analyticsService.track(
      EventTrackingEnum.JOBPOST_STARTED,
      {
        job_type: 'last minute job',
        event_id: this.scheduledEventDerivedUniqueId,
        scheduled_event_occurrence_id: this.scheduledEventOccurrenceId,
      },
      AnalyticsServicesNameEnum.SEGMENT_SERVICE,
    );
  }

  /**
   * Fetch users latest location details and set into input field
   */
  private async getLocationDetails(): Promise<void> {
    const consumerDetails: ConsumerAccount = await this.consumerAccountService.getConsumerDetails('supportTakePlace');
    if (consumerDetails) {
      // get suburb details from current user address and fill into input field
      const addressDetails = await this.autoCompleteService.search(AutoCompleteDataSourceEnum.SUBURB, {
        searchString: `${consumerDetails.profile.participantAddressLine2} ${consumerDetails.profile.participantPostcode}`,
      });
      if (addressDetails?.length) {
        const searchFilterForm = <UntypedFormGroup>this.form.controls['search_filter'];
        searchFilterForm.get('suburb').setValue({
          txt: addressDetails[0].address,
          idx: addressDetails[0].post_code_geo_id,
        });

        this.form.controls['post_code_geo_id'].setValue(addressDetails[0].post_code_geo_id);
        this.form.controls['job_location'].setValue(addressDetails[0].address);
      }
    }
  }

  /**
   * If this page is redirected from the cancellation or decline of a scheduled session, then pre-populate the form
   * (as much as possible) by calling the prefillLastMinuteJobFromSession API with the derivedUniqueId of the cancelled
   * session to get the prefill data from the posted job linked to the agreement between the user and the SP who
   * cancelled the session.
   */
  async prefillLmjInfoFromSession(): Promise<void> {
    const scheduledEventDerivedUniqueId =
      this.activatedRoute.snapshot.queryParamMap.get(SCHEDULED_EVENT_DERIVED_UNIQUE_ID_PARAM) || null;
    if (!scheduledEventDerivedUniqueId) {
      return;
    }

    // It's essential to decode to replace the url-safe characters back to its original character so
    // the backend can correctly find the scheduled event.
    this.scheduledEventDerivedUniqueId = decodeURIComponent(scheduledEventDerivedUniqueId);

    // Store to be used for tracking LMJ created from cancelled sessions
    this.scheduledEventOccurrenceId =
      this.activatedRoute.snapshot.queryParamMap.get(SCHEDULED_EVENT_OCCURRENCE_ID_PARAM) || null;

    try {
      const prefillData = await this.flexJobsService.fetchLastMinuteJobInfoFromSession(
        this.scheduledEventDerivedUniqueId,
      );

      if (prefillData) {
        /**
         * If some prefill data fields are NULLISH (i.e. title can be NULL if there are no related posted job), then the existing
         * form values are retained as the user could have already inputted values (i.e. before retrying the prefill from the error banner)
         */
        this.form.patchValue({
          title: prefillData.title || this.form.value.title,
          start_date: this.getStartDate(moment(prefillData.startTime)),
          start_time: this.getStartTime(prefillData.startTime),
          duration: prefillData.duration || this.form.value.duration,
          consumer_description: prefillData.consumerDescription || this.form.value.consumer_description,
          job_description: prefillData.jobDescription || this.form.value.job_description,
          gender: prefillData.gender || this.form.value.gender,
          search_filter: {
            advanced: {
              gender: prefillData.gender || this.form.value.gender,
            },
            basic_search: this.getRelevantServices(prefillData.services) || this.form.value.services,
          },
        });

        // evaluate the status of the prefilling
        const isSessionStartTimeWithinRange = moment(prefillData.startTime).diff(moment(), 'hour') >= 4;
        this.prefillLmjInfoFromSessionContext.bannerStatus = isSessionStartTimeWithinRange
          ? PrefillLmjInfoFromSessionBannerStatus.PREFILL
          : PrefillLmjInfoFromSessionBannerStatus.OUT_OF_RANGE;
      }
    } catch (error) {
      console.error(error);
      this.prefillLmjInfoFromSessionContext.bannerStatus = PrefillLmjInfoFromSessionBannerStatus.ERROR;
    } finally {
      this.prefillLmjInfoFromSessionContext.hasBeenLoadedAtLeastOnce = true;
    }
  }

  /**
   * Close the LMJ Prefill banner.
   */
  closePrefillLMJBanner(): void {
    this.prefillLmjInfoFromSessionContext.hasBeenLoadedAtLeastOnce = false;
  }

  /**
   * Given the moment representation of the start date and time, calculate the support date displayed to the client.
   * If the start date and time is in the past, return 'Today'.
   *
   * @param startTime - the Moment value of the start date and time.
   * @returns support date in the form of 'Today', 'Tomorrow' or 'Day_After_Tomorrow'.
   */
  private getStartDate(startTime: moment.Moment): string {
    const today = moment().startOf('day');
    const timeDiff = startTime?.startOf('day').diff(today, 'days') ?? 0;

    switch (timeDiff) {
      case 2:
        return moment().add(2, 'days').format(this.dateFormat);
      case 1:
        return moment().add(1, 'days').format(this.dateFormat);
      case 0:
      default:
        return moment().format(this.dateFormat);
    }
  }

  /**
   * Given the start date and time in string format, check if the start date and time is in the past,
   * return the nearest future hour to the current time if it is, otherwise return it as it is.
   *
   * @param startTime - the string value of the start date and time.
   * @returns support start time.
   */
  private getStartTime(startTime: string): string {
    const startTimeMoment = moment(startTime);

    // Return nearest hour in the future if startTime is in the past or if it's not valid
    if (!startTimeMoment.isValid() || startTimeMoment.diff(moment()) < 0) {
      return moment().add(1, 'hour').startOf('hour').format();
    }

    return startTime;
  }

  /**
   * Given an array of possible services, filter it down to the services that are available in the serviceTemplates.
   * This is due to the fact that the services returned from the prefillLastMinuteJobFromSession API can contain
   * additional services (from a standard job) that are not available on a last minute job.
   *
   * @param services - the array of services to be filtered.
   * @returns the filtered array of services.
   */
  private getRelevantServices(services: string[]): string[] {
    return services?.filter((service) => !!flexServices().find((template) => template.id === service)) || [];
  }

  updateServices(services) {
    const fg = <UntypedFormGroup>this.form.controls['search_filter'];
    fg.controls['basic_search'].setValue(services);
  }

  /**
   * Show error message.
   *
   * @param response - Error response container.
   */
  showErrors(response) {
    let errMsgs;

    // list of error messages for the fields having error.
    if (response?.graphQLErrors[0]?.extensions?.code === 'VALIDATION_ERROR') {
      errMsgs = response.graphQLErrors[0].extensions?.validation || {};
      for (const errorMsgsKeys in errMsgs) {
        // set errormessage for the form field
        this.form.get(this.errorMessageKeyMapping[errorMsgsKeys])?.setErrors({ errorMessage: errMsgs[errorMsgsKeys] });
      }
      this.showAlert('Invalid Job', 'Please fill in highlighted fields');
    } else {
      this.showErrorAlert(
        `There's been an error processing your job post. Please try again later. If the problem persists, please contact ${settings.brand.name} for support.`,
      );
    }
  }

  /**
   * Handles tracking analytics for posted jobs.
   *
   * @param postedJob - posted job data
   */
  private handlePostedJobsForAnalytics(postedJob): void {
    const postedWithin14DaysOfUserSignUp = postedJob.clientFields.postedWithin14DaysOfUserSignUp;
    const firstPostedJob = postedJob.clientFields.firstPostedJob;
    // Call the method to track additional analytics based on whether it's the first job within 14 days of user sign up.
    this.postedWithin14DaysOfUserSignUpForAnalytics(postedWithin14DaysOfUserSignUp, firstPostedJob);

    // Call the method to track additional analytics based on whether it's the first posted job.
    this.firstPostedJobForAnalytics(firstPostedJob, postedJob?.id);
  }

  /**
   * Track analytics for the first job within 14 days of user sign up.
   *
   * @param postedWithin14DaysOfUserSignUp - indicates if it's the first job within 14 days of user sign up
   * @param firstPostedJob - indicates if it's the first posted job.
   */
  private postedWithin14DaysOfUserSignUpForAnalytics(
    postedWithin14DaysOfUserSignUp: boolean,
    firstPostedJob: boolean,
  ): void {
    if (postedWithin14DaysOfUserSignUp && firstPostedJob) {
      this.analyticsService.track('signup_client_high_intent', { member_type: 'client' });
    }
  }

  /**
   * Track analytics for the first posted job.
   *
   * @param firstPostedJob - indicates if it's the first posted job.
   * @param postedJobId - job id of posted job.
   */
  private firstPostedJobForAnalytics(firstPostedJob: boolean, postedJobId: number | string): void {
    if (firstPostedJob) {
      this.analyticsService.track(
        EventTrackingEnum.FIRST_JOBPOST_COMPLETED,
        { member_type: 'client', job_type: 'last minute job', job_id: postedJobId },
        AnalyticsServicesNameEnum.SEGMENT_SERVICE,
      );
      this.analyticsService.track(
        'client_firstjob',
        { member_type: 'client' },
        AnalyticsServicesNameEnum.FIREBASE_ANALYTICS_SERVICE,
      );
      this.analyticsService.identify({
        is_activated: true,
      });
    }
  }

  /**
   * Submit Last Minute Job
   *
   */
  async process() {
    this.submitAttempt = true;

    if (this.validForm()) {
      this.showLoading();
      try {
        // Creating request object to be passed
        const createFlexJobRequestObject: JobCreateUpdateRequest = this.setRequestObj();
        // grahpql mutation to create last minute job
        const res = await this.flexJobsService.createLastMinuteJob(createFlexJobRequestObject);
        if (res) {
          this.handlePostedJobsForAnalytics(res);
          this.analyticsService.track(
            EventTrackingEnum.JOBPOST_COMPLETED,
            {
              job_type: 'last minute job',
              job_id: res?.id,
              event_id: this.scheduledEventDerivedUniqueId,
              scheduled_event_occurrence_id: this.scheduledEventOccurrenceId,
            },
            AnalyticsServicesNameEnum.SEGMENT_SERVICE,
          );
          const navigateExtraOptions = {
            queryParams: this.createQueryParams(),
          };
          this.router.navigate([`/flex/jobs/${res?.status?.clientStatus}/consumer`, res?.id], navigateExtraOptions);
        }
      } catch (error) {
        // Show account restriction modal when received ACTOR_SUSPENDED code in graphQL error code.
        if (isGraphQLErrorCode(error, GQLErrorCodeEnum.ACTOR_SUSPENDED)) {
          this.softBanService.openSoftBanModal();
        } else {
          this.showErrors(error);
        }
      } finally {
        this.hideLoading();
      }
    } else {
      this.showAlert('Invalid Job', 'Please fill in highlighted fields');
      this.form.markAllAsTouched();
    }
  }

  closePage() {
    this.router.navigate(['tabs/jobs']);
  }

  validPostCode() {
    return this.job.id ? true : this.form.value.search_filter.suburb.idx;
  }

  private validForm() {
    return this.form.valid && this.validPostCode();
  }

  suburbChanged(suburb) {
    const fg = <UntypedFormGroup>this.form.controls['search_filter'];
    fg.controls['suburb'].setValue(suburb);

    this.form.controls['post_code_geo_id'].setValue(suburb.idx);
    this.form.controls['job_location'].setValue(suburb.txt);
  }

  getSuburbName() {
    return this.form.value.search_filter.suburb.txt;
  }

  /**
   * Creates the query params for redirecting to the view job page after the job has been successfully created.
   * If the job is created from a cancelled or declined session, then return source as 'calendar'.
   *
   * @returns query params to be used for the redirection.
   */
  private createQueryParams(): Record<string, string> {
    const queryParams = {};
    if (this.scheduledEventDerivedUniqueId || this.scheduledEventOccurrenceId) {
      queryParams['source'] = 'calendar';
    }

    return queryParams;
  }

  /**
   * Set request object.
   *
   */
  private setRequestObj() {
    const formValue = { ...this.form.value };

    // Start date is already set and formatted through form control so we just set the start time
    const startTime = `${moment(this.form.value.start_date, this.dateFormat).format('DD-MM-YYYY')} ${moment(
      this.form.value.start_time,
    ).format('HH:mm')}`;

    // Add duration to start_time so you get the end date
    const endTime = moment(startTime, this.timeFormat).add(this.form.value.duration, 'hours').format(this.timeFormat);
    const createFlexJobObject: JobCreateUpdateRequest = {
      action: 'create',
      attributes: {
        title: formValue.title,
        jobDescription: formValue.job_description,
        consumerDescription: formValue.consumer_description,
        endTime,
        searchFilter: {
          basicSearch: formValue.search_filter.basic_search,
          suburb: {
            idx: formValue.search_filter.suburb.idx,
            txt: formValue.search_filter.suburb.txt,
          },
          advanced: {
            gender: formValue?.gender?.toLowerCase(),
          },
        },
        startTime,
        timezoneOffset: new Date().getTimezoneOffset(),

        // properties below are mainly used for tracking
        ...this.getCreateJobTrackingProps(),
      },
    };

    // no need to send gender incase it has no value
    if (!createFlexJobObject['attributes']['searchFilter']['advanced']['gender']) {
      delete createFlexJobObject['attributes']['searchFilter']['advanced']['gender'];
    }

    return createFlexJobObject;
  }

  /**
   * On require support value change updating 'start_time' form control validators.
   *
   */
  onChangeRequireSupport() {
    // Whenever a lmj job is submitted and if the job start time selected fall within the next 4 hours then we set the backend error message in the 'start_time' control and shown it as a validation message, Refer: showErrors().
    // but we also need to update the 'start_time' control to the default validations(if already errorMessage has been applied there) as soon as the require support options value changes
    // because the already applied backend error message needs to be clear out and new error from backend after the lmj post should be applied.
    if (this.form.get('start_time')?.errors?.errorMessage) {
      this.form.get('start_time').setValidators([Validators.required]);
      this.form.get('start_time').updateValueAndValidity();
    }
  }

  /**
   * Generates the properties used for tracking the creation of flex job.
   *
   * @returns A PostJobQueryParams object containing all the available tracking properties.
   */
  private getCreateJobTrackingProps(): PostJobQueryParams {
    return {
      source: this.activatedRoute.snapshot.queryParamMap.get(POSTJOB_SOURCE_QUERY_PARAM) || undefined,
      origin: this.activatedRoute.snapshot.queryParamMap.get(POSTJOB_ORIGIN_QUERY_PARAM) || undefined,
      scheduledEventDerivedUniqueId: this.scheduledEventDerivedUniqueId || undefined,
      scheduledEventOccurrenceId: this.scheduledEventOccurrenceId || undefined,
    };
  }
}
